import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Globe } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  // Load from cache first for instant display
  const [siteSettings, setSiteSettings] = useState(() => {
    try {
      const cached = localStorage.getItem('site_settings');
      return cached ? JSON.parse(cached) : null;
    } catch { return null; }
  });
  const { t, language, changeLanguage } = useLanguage();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Fetch site settings (logo) and cache
  useEffect(() => {
    fetch(`${BACKEND_URL}/api/site-settings`)
      .then(res => res.json())
      .then(data => {
        setSiteSettings(data);
        try { localStorage.setItem('site_settings', JSON.stringify(data)); } catch {}
      })
      .catch(() => {});
  }, []);

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.pathname]);

  const navLinks = [
    { path: '/', label: t('nav.home') },
    { path: '/equipment', label: t('nav.equipment') },
    { path: '/services', label: t('nav.services') },
    { path: '/references', label: t('nav.references') },
    { path: '/partners', label: t('nav.partners') },
    { path: '/become-partner', label: t('nav.becomePartner') },
    { path: '/client-feedback', label: t('nav.clientFeedback') },
    { path: '/events', label: t('nav.events') },
    { path: '/contact', label: t('nav.contact') },
  ];

  const isActive = (path) => location.pathname === path;

  const handleNavClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setIsOpen(false);
  };

  return (
    <header className={`fixed w-full z-50 transition-all duration-500 ${scrolled ? 'bg-slate-50/95 backdrop-blur-md shadow-sm' : 'bg-slate-50'}`}>
      {/* Main navigation */}
      <div className="container mx-auto px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" onClick={handleNavClick} className="flex items-center gap-3 min-h-[44px]">
            {siteSettings?.logo_header && (
              <img 
                src={siteSettings.logo_header} 
                alt={siteSettings.logo_alt || "SAE"} 
                className="h-10 w-auto object-contain"
              />
            )}
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden xl:flex items-center gap-8">
            {navLinks.slice(0, 5).map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={handleNavClick}
                className={`text-sm font-medium transition-all duration-300 relative py-2 ${
                  isActive(link.path)
                    ? 'text-slate-900'
                    : 'text-slate-500 hover:text-slate-900'
                }`}
              >
                {link.label}
                {isActive(link.path) && (
                  <span className="absolute bottom-0 left-0 w-full h-0.5 bg-slate-900" />
                )}
              </Link>
            ))}
            
            {/* More dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger className="text-sm font-medium text-slate-500 hover:text-slate-900 flex items-center gap-1 outline-none transition-colors duration-300">
                {language === 'en' ? 'More' : 'Plus'}
                <ChevronDown size={14} />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-white border-slate-200">
                {navLinks.slice(5).map((link) => (
                  <DropdownMenuItem key={link.path} asChild>
                    <Link
                      to={link.path}
                      onClick={handleNavClick}
                      className={`w-full text-sm ${
                        isActive(link.path) ? 'text-slate-900 font-medium' : 'text-slate-600'
                      }`}
                    >
                      {link.label}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </nav>

          {/* Language Toggle & Mobile Menu Button */}
          <div className="flex items-center gap-4">
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors duration-300 border border-slate-200 rounded-full hover:border-slate-300 outline-none">
                <Globe size={16} />
                <span>{language === 'fr' ? 'FR' : language === 'en' ? 'EN' : 'AR'}</span>
                <ChevronDown size={12} />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-white border-slate-200 min-w-[120px]">
                <DropdownMenuItem 
                  onClick={() => changeLanguage('fr')}
                  className={`cursor-pointer ${language === 'fr' ? 'bg-slate-100 font-medium' : ''}`}
                >
                  🇫🇷 Français
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => changeLanguage('en')}
                  className={`cursor-pointer ${language === 'en' ? 'bg-slate-100 font-medium' : ''}`}
                >
                  🇬🇧 English
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => changeLanguage('ar')}
                  className={`cursor-pointer ${language === 'ar' ? 'bg-slate-100 font-medium' : ''}`}
                >
                  🇩🇿 العربية
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <button
              onClick={() => setIsOpen(!isOpen)}
              className="xl:hidden p-2 text-slate-600 hover:text-slate-900 transition-colors"
              aria-label={isOpen ? "Fermer le menu" : "Ouvrir le menu"}
              aria-expanded={isOpen}
            >
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="xl:hidden py-6 border-t border-slate-200">
            <nav className="flex flex-col gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={handleNavClick}
                  className={`px-4 py-3 text-sm font-medium transition-colors rounded-lg ${
                    isActive(link.path)
                      ? 'text-slate-900 bg-slate-200/50'
                      : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200/50'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
